<script setup lang="ts">
    import { computed, useTemplateRef } from 'vue';
    import { useSqlEditorStore } from '@/stores/sqlEditorStore.ts';
    import { SavedQueryHeaderInfo } from '@/shared/helpers/api';
    import { buildFolderStructure } from '@/shared/helpers/poorMansFolderStructure';
    import SavedQueryTreeItem from './SavedQueryTreeItem.vue';
    import { useRouter } from 'vue-router';
    import { getSqlEditorLinkBySavedQuery } from '@/shared/helpers/routing';
    import { BDropdown } from 'buefy';

    const store = useSqlEditorStore();
    const loadButtonDialog = useTemplateRef<typeof BDropdown>('loadButtonDialog');
    const router = useRouter();

    function queryPath(query: SavedQueryHeaderInfo): string {
        if (!query.isPublic && query.isShared && query.userId !== store.userId) {
            return `Shared queries/${query.userName}/${query.name}`;
        }
        return `${query.isPublic ? 'Public queries' : 'My queries'}/${query.name}`;
    }

    const folders = computed(() => {
        if (store.savedQueriesAll) {
            return buildFolderStructure(store.savedQueriesAll, queryPath);
        }
        return [];
    });

    async function loadQuery(q: SavedQueryHeaderInfo): Promise<void> {
        await router.push(getSqlEditorLinkBySavedQuery(q.guid));
        close();
    }

    function close(): void {
        loadButtonDialog.value?.toggle();
    }
</script>

<template>
    <b-dropdown
        ref="loadButtonDialog"
        position="is-bottom-right"
        append-to-body
        aria-role="dialog"
        trap-focus>
        <template #trigger>
            <button
                class="button"
                role="button">
                <b-icon
                    icon="folder"
                    size="is-small" />
                <span>Open</span>
            </button>
        </template>

        <b-dropdown-item
            aria-role="listitem"
            :focusable="false"
            custom
            paddingless>
            <div
                class="card"
                style="width:400px;">
                <section class="card-content is-size-6">
                    <p class="block">
                        Click a query below to load it into the SQL editor.
                    </p>
                    <div class="block">
                        <ul v-if="folders.length > 0">
                            <saved-query-tree-item
                                v-for="(item, idx) in folders"
                                :key="idx"
                                :item="item"
                                :is-bold="true"
                                :user-id="store.userId"
                                :query-click-callback="loadQuery" />
                        </ul>
                        <span v-else>
                            There are no saved queries available.
                        </span>
                    </div>
                </section>
                <footer class="card-footer">
                    <div class="card-footer-item is-justify-content-end">
                        <b-field grouped>
                            <button
                                class="button"
                                @click="close">
                                Close
                            </button>
                        </b-field>
                    </div>
                </footer>
            </div>
        </b-dropdown-item>
    </b-dropdown>
</template>
